// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import {
  SectionHeader,
  EditableSelectComponent,
  EditableTextareaComponent,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { useSession } from "@clerk/clerk-react";
import { FileLabel } from "shared/types/file";

import {
  BuildingChimneyStabilityStatus,
  BuildingConcreteStatus,
  BuildingCracksAndFissuresStatus,
  BuildingFloorStabilityStatus,
  BuildingInspectionInfo,
  BuildingRoofStabilityStatus,
  BuildingSubsidenceStatus,
  SteelGirdersStatus,
} from "shared/types/building-inspection";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface BuildingStabilityInfoComponentProps {
  inspectionInfo: BuildingInspectionInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingStabilityInfoComponent = ({
  inspectionInfo,
  handleCloseParent,
  building,
  unit,
  handleInspectionInfoChangeInParent,
  parentEditMode = true,
}: BuildingStabilityInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedInspectionInfo, setEditedInspectionInfo] =
    useState(inspectionInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);

  useEffect(() => {
    console.log(inspectionInfo);
    setEditedInspectionInfo(inspectionInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/building/building-inspection-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedInspectionInfo),
        },
        session,
      );
      toast.success("Stabiliteit informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de stabiliteit informatie.",
      );
    }
  };

  const handleOpenAddStabilityDocumentModal = () => {
    setFileModalOpen(true);
  };

  const updateInspectionInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedInspectionInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...inspectionInfo,
        ...updatedFields,
      });
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case BuildingSubsidenceStatus.OK:
      case BuildingConcreteStatus.OK:
      case BuildingConcreteStatus.NOT_REQUIRED:
      case SteelGirdersStatus.OK:
      case SteelGirdersStatus.NOT_REQUIRED:
      case BuildingCracksAndFissuresStatus.OK:
      case BuildingRoofStabilityStatus.OK:
      case BuildingFloorStabilityStatus.OK:
      case BuildingChimneyStabilityStatus.OK:
      case BuildingChimneyStabilityStatus.NOT_REQUIRED:
        return "bg-green-500";
      case BuildingSubsidenceStatus.NOT_OK:
      case BuildingConcreteStatus.NOT_OK:
      case SteelGirdersStatus.NOT_OK:
      case BuildingCracksAndFissuresStatus.NOT_OK:
      case BuildingRoofStabilityStatus.NOT_OK:
      case BuildingFloorStabilityStatus.NOT_OK:
      case BuildingChimneyStabilityStatus.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const handleFileModalClose = () => {
    setFileModalOpen(false);
  };

  const handleFileModalSave = async (fileLogData: any) => {
    const newStabilityFileLogs =
      fileLogData.customData?.savedBuildingInspectionInfo.stabilityfileLogs;
    if (newStabilityFileLogs) {
      setEditedInspectionInfo((prev) => ({
        ...prev,
        stabilityfileLogs: newStabilityFileLogs,
      }));
    }
    handleFileModalClose();
  };

  const onUpdateBuildingCracksAndFissuresDescription = (remarks: string) => {
    updateInspectionInfo({ buildingCracksAndFissuresDescription: remarks });
  };

  // Placeholder for getFileSection similar to CadastreInfoComponent

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Stabiliteit Informatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* Editable selects and inputs for each stability field */}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingSubsidences,
                )}`}
              />

              <p>Zijn er verzakkingen aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingSubsidenceStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.buildingSubsidences}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingSubsidences: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.steelGirdersStatus,
                )}`}
              />

              <p>Stalen liggers toestand</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(SteelGirdersStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.steelGirdersStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ steelGirdersStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingConcreteStatus,
                )}`}
              />

              <p>Betonstructuren toestand</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingConcreteStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.buildingConcreteStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingConcreteStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingCracksAndFissures,
                )}`}
              />

              <p>Scheuren of gaten vaststelbaar in het gebouw?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingCracksAndFissuresStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.buildingCracksAndFissures}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingCracksAndFissures: value })
              }
            />
          </div>
          {editedInspectionInfo?.buildingCracksAndFissures ==
            BuildingCracksAndFissuresStatus.NOT_OK && (
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <p>Beschrijving waar de scheuren of gaten werden vastgesteld</p>
                </header>
                <EditableTextareaComponent
                  label="Geef opmerkingen of toelichting hier"
                  value={
                    editedInspectionInfo?.buildingCracksAndFissuresDescription ||
                    ""
                  }
                  isLoading={false}
                  editMode={editMode}
                  onChange={onUpdateBuildingCracksAndFissuresDescription}
                />
              </div>
            )}
          {editedInspectionInfo?.buildingCracksAndFissures ==
            BuildingCracksAndFissuresStatus.NOT_OK && (
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <p>Foto's van eventuele barsten en scheuren</p>
                </header>
                <ImagesContainer
                  buildingId={building.id as string}
                  fileLabel={FileLabel.StabilityPictureCracksAndFissures}
                />
              </div>
            )}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingRoofStabilityStatus,
                )}`}
              />

              <p>Staat dak</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingRoofStabilityStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.buildingRoofStabilityStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingRoofStabilityStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingFloorStabilityStatus,
                )}`}
              />

              <p>Staat scheidingsvloeren tussenverdiepingen</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingFloorStabilityStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.buildingFloorStabilityStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingFloorStabilityStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.buildingChimneyStabilityStatus,
                )}`}
              />

              <p>Staat schoorsteen</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BuildingChimneyStabilityStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.buildingChimneyStabilityStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ buildingChimneyStabilityStatus: value })
              }
            />
          </div>
          {/* Additional fields similar to the above with their respective handlers */}
          {/* File upload logic */}
          <div className="col-span-full mt-8 flex flex-col gap-3">
            <FileLogTable
              acceptedLabels={[FileLabel.StabilityStudy, FileLabel.StabilityCommunication, FileLabel.StabilityReportVisit]}
              onUploadClick={handleOpenAddStabilityDocumentModal}
              building={building}
            />
          </div>
          {/* Modal for File Upload */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={() => setFileModalOpen(false)}
            onSave={(fileLogData) => handleFileModalSave(fileLogData)}
            building={building}
            unit={unit}
            incomingCustomData={{
              buildingInspectionInfoId: editedInspectionInfo?.id,
            }}
            customLabels={[
              FileLabel.StabilityStudy,
              FileLabel.StabilityCommunication,
              FileLabel.StabilityReportVisit,
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default BuildingStabilityInfoComponent;
